import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { Image } from "hesus-ui-kit";
import { avatarIcons, avatarIconsMr, avatarIconsMs } from "@utils/avatarInfo";
import {
  RecipientsOuter,
  RecipientCard,
  RecipientInfo,
  RecipientName,
  RecipientEmail,
  RecipientNoEmail,
  PlaceholderText,
} from "./CompanySite.styled";

export const INVOICING_CONTACT_TYPE_ID = 1;

const getAvatarImage = (contact) => {
  const avatarNumber = (contact.contactId ?? contact.id ?? 0) % 10;
  if (contact?.courtesy === "MISTER") {
    return avatarIconsMr[avatarNumber] ?? avatarIcons[1];
  }
  if (contact?.courtesy === "MISS") {
    return avatarIconsMs[avatarNumber] ?? avatarIcons[4];
  }
  return avatarIcons[avatarNumber] ?? avatarIcons[1];
};

const Recipients = ({
  t,
  companyId,
  visible,
  selectedContacts,
  onChangeSelectedContacts,
}) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || !companyId) {
      setContacts([]);
      return;
    }
    setLoading(true);
    axios
      .get(
        `/allContacts?companyId=${companyId}&typeIds=[${INVOICING_CONTACT_TYPE_ID}]`
      )
      .then((response) => {
        const mappedContacts = (response.data?.contacts || []).map(
          (contact) => ({
            ...contact,
            contactId: contact.id,
            id: null,
          })
        );
        setContacts(mappedContacts);
      })
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  }, [visible, companyId]);

  if (!visible) return null;

  const contactIdMatches = (a, b) => String(a) === String(b);

  const toggleContact = (contact) => {
    const isSelected = selectedContacts.some(
      (c) => contactIdMatches(c.contactId, contact.contactId)
    );
    if (isSelected) {
      onChangeSelectedContacts(
        selectedContacts.filter(
          (c) => !contactIdMatches(c.contactId, contact.contactId)
        )
      );
    } else {
      onChangeSelectedContacts([...selectedContacts, contact]);
    }
  };

  return (
    <div>
      {!loading && contacts.length === 0 && (
        <PlaceholderText>
          {!companyId
            ? t("CLIENT_INVOICING_CONTACT_NO_COMPANY")
            : t("CLIENT_INVOICING_NO_INVOICING_CONTACT")}
        </PlaceholderText>
      )}
      {contacts.length > 0 && (
        <RecipientsOuter>
          {contacts.map((contact) => {
            const isSelected = selectedContacts.some(
              (c) => contactIdMatches(c.contactId, contact.contactId)
            );
            return (
              <RecipientCard
                key={contact.contactId}
                $isSelected={isSelected}
                onClick={() => toggleContact(contact)}
              >
                <Image
                  src={getAvatarImage(contact)}
                  alt="Avatar"
                  styles={{ height: "40px", width: "40px" }}
                />
                <RecipientInfo>
                  <RecipientName>
                    {`${contact.firstName || ""} ${
                      contact.lastName || ""
                    }`.trim()}
                  </RecipientName>
                  {contact.email ? (
                    <RecipientEmail>{contact.email}</RecipientEmail>
                  ) : (
                    <RecipientNoEmail>
                      {t("CLIENT_INVOICING_CONTACT_NO_EMAIL")}
                    </RecipientNoEmail>
                  )}
                </RecipientInfo>
              </RecipientCard>
            );
          })}
        </RecipientsOuter>
      )}
    </div>
  );
};

Recipients.propTypes = {
  t: PropTypes.func.isRequired,
  companyId: PropTypes.number,
  visible: PropTypes.bool,
  selectedContacts: PropTypes.array,
  onChangeSelectedContacts: PropTypes.func.isRequired,
};

Recipients.defaultProps = {
  companyId: null,
  visible: false,
  selectedContacts: [],
};

export default withTranslation()(Recipients);
